(function () {
  'use strict';

  const MOBILE_BP = 768;

  const isMobile = () => window.innerWidth <= MOBILE_BP;
  const noMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* =========================================================
     SimonsProtocol
  ========================================================= */
  class SimonsProtocol {
    constructor(section) {
      this.section      = section;
      this.tabs         = Array.from(section.querySelectorAll('.sp-tab'));
      this.panels       = Array.from(section.querySelectorAll('.desktop-panels .sp-panel'));
      this.sliderEl     = section.querySelector('.mobile-slider');
      this.slickReady   = false;
      this.activeIndex  = 0;
      this._fromSlick   = false; // guard: slider triggered the change
      this._fromTab     = false; // guard: tab triggered the change

      if (!this.tabs.length) return;

      this._initTabStates();
      this._bindTabs();
      this._maybeInitSlick();
      this._bindResize();
    }

    /* -------------------------------------------------------
       Set initial ARIA states on all tabs
    ------------------------------------------------------- */
    _initTabStates() {
      this.tabs.forEach((tab, i) => {
        tab.setAttribute('tabindex', i === 0 ? '0' : '-1');
      });
    }

    /* -------------------------------------------------------
       Tab click & keyboard
    ------------------------------------------------------- */
    _bindTabs() {
      this.tabs.forEach((tab, i) => {
        tab.addEventListener('click', () => this._tabClicked(i));

        tab.addEventListener('keydown', (e) => {
          const count = this.tabs.length;
          let target = null;
          switch (e.key) {
            case 'ArrowRight': case 'ArrowDown':
              e.preventDefault(); target = (i + 1) % count; break;
            case 'ArrowLeft':  case 'ArrowUp':
              e.preventDefault(); target = (i - 1 + count) % count; break;
            case 'Home':
              e.preventDefault(); target = 0; break;
            case 'End':
              e.preventDefault(); target = count - 1; break;
          }
          if (target !== null) {
            this._tabClicked(target);
            this.tabs[target].focus();
          }
        });
      });
    }

    /* -------------------------------------------------------
       Called when a tab is clicked/keyboard-activated
    ------------------------------------------------------- */
    _tabClicked(index) {
      if (index === this.activeIndex) return; // nothing to do

      this._updateTabs(index);
      this._updatePanels(index);
      this._scrollTabIntoView(index);

      // Sync slider WITHOUT triggering afterChange → _tabClicked loop
      if (isMobile() && this.slickReady && this.sliderEl && window.jQuery) {
        this._fromTab = true;
        window.jQuery(this.sliderEl).slick('slickGoTo', index, /* no animation= */ noMotion());
      }

      this.activeIndex = index;
    }

    /* -------------------------------------------------------
       Called when Slick fires afterChange (swipe/drag)
    ------------------------------------------------------- */
    _sliderChanged(index) {
      if (this._fromTab) {
        // This afterChange was triggered by our slickGoTo call — ignore
        this._fromTab = false;
        return;
      }
      if (index === this.activeIndex) return;

      this._updateTabs(index);
      this._updatePanels(index);
      this._scrollTabIntoView(index);
      this.activeIndex = index;
    }

    /* -------------------------------------------------------
       DOM update helpers
    ------------------------------------------------------- */
    _updateTabs(index) {
      this.tabs.forEach((tab, i) => {
        const active = i === index;
        tab.classList.toggle('is-active', active);
        tab.setAttribute('aria-selected', active ? 'true' : 'false');
        tab.setAttribute('tabindex', active ? '0' : '-1');
      });
    }

    _updatePanels(index) {
      this.panels.forEach((panel, i) => {
        const active = i === index;
        panel.classList.toggle('is-active', active);
        if (active) {
          panel.removeAttribute('hidden');
        } else {
          panel.setAttribute('hidden', '');
        }
      });
    }

    _scrollTabIntoView(index) {
      if (!isMobile()) return;
      const tab = this.tabs[index];
      if (!tab) return;
      tab.scrollIntoView({
        behavior: noMotion() ? 'auto' : 'smooth',
        block: 'nearest',
        inline: 'center',
      });
    }

    /* -------------------------------------------------------
       Slick init / destroy
    ------------------------------------------------------- */
    _maybeInitSlick() {
      if (isMobile()) this._initSlick();
    }

    _initSlick() {
      if (!window.jQuery || !window.jQuery.fn || !window.jQuery.fn.slick) {
        // Poll until Slick is available (max 3 s)
        let attempts = 0;
        const poll = setInterval(() => {
          attempts++;
          if (window.jQuery && window.jQuery.fn && window.jQuery.fn.slick) {
            clearInterval(poll);
            this._setupSlick();
          }
          if (attempts > 30) clearInterval(poll);
        }, 100);
        return;
      }
      this._setupSlick();
    }

    _setupSlick() {
      if (!this.sliderEl || !window.jQuery) return;
      const $ = window.jQuery;
      const $slider = $(this.sliderEl);

      if ($slider.hasClass('slick-initialized')) return;

      $slider.slick({
        dots:            true,
        arrows:          false,
        infinite:        false,
        // adaptiveHeight DISABLED — it causes height jumps when switching slides.
        // Use a fixed min-height via CSS instead.
        adaptiveHeight:  false,
        speed:           noMotion() ? 0 : 320,
        cssEase:         'cubic-bezier(0.25, 0.46, 0.45, 0.94)',
        slidesToShow:    1,
        slidesToScroll:  1,
        draggable:       true,
        swipe:           true,
        touchThreshold:  10,
        initialSlide:    this.activeIndex,
        waitForAnimate:  true, // prevent rapid-fire jumps
      });

      this.slickReady = true;

      // Only respond to genuine user swipes, not programmatic slickGoTo calls
      $slider.on('afterChange', (_e, _slick, currentSlide) => {
        this._sliderChanged(currentSlide);
      });
    }

    _destroySlick() {
      if (!this.sliderEl || !window.jQuery) return;
      const $slider = window.jQuery(this.sliderEl);
      if ($slider.hasClass('slick-initialized')) {
        $slider.slick('unslick');
        this.slickReady = false;
        this._fromTab   = false;
      }
    }

    /* -------------------------------------------------------
       Resize handler
    ------------------------------------------------------- */
    _bindResize() {
      let timer;
      window.addEventListener('resize', () => {
        clearTimeout(timer);
        timer = setTimeout(() => {
          if (!isMobile() && this.slickReady) {
            this._destroySlick();
            // Re-assert desktop panel state
            this._updatePanels(this.activeIndex);
            this._updateTabs(this.activeIndex);
          } else if (isMobile() && !this.slickReady) {
            this._initSlick();
          }
        }, 160);
      });
    }
  }

  /* =========================================================
     Boot
  ========================================================= */
  function init() {
    document.querySelectorAll('.sp-section[data-section-type="simons-protocol"]').forEach((section) => {
      if (!section._spInstance) {
        section._spInstance = new SimonsProtocol(section);
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Shopify Theme Editor events
  document.addEventListener('shopify:section:load', (e) => {
    const section = e.target.querySelector('.sp-section[data-section-type="simons-protocol"]');
    if (section) {
      delete section._spInstance;
      section._spInstance = new SimonsProtocol(section);
    }
  });

  document.addEventListener('shopify:section:select', (e) => {
    const section = e.target.querySelector('.sp-section[data-section-type="simons-protocol"]');
    if (section?._spInstance) {
      section._spInstance._updateTabs(0);
      section._spInstance._updatePanels(0);
    }
  });

})();